import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import type { Direction } from '@/types/game'
import type { GamePhase } from '@/hooks/useGameEngine'
import type { CharacterShape } from '@/game/characters'
import { EmberProps3D } from './EmberProps3D'
import { makeRoundedBox } from './roundedBoxGeometry'

interface EmberProps {
  color: string
  phase: GamePhase
  slideDir: [number, number] | null
  facing: Direction
  reducedMotion: boolean
  shape?: CharacterShape
  bonkKey?: number
  energy?: number
  characterId?: string
  billboard?: boolean
  equippedProps?: string[]
  graphics?: 'high' | 'medium' | 'low'
}

const DEFAULT_SHAPE: CharacterShape = {
  bodyScale: 1,
  eyeScale: 1,
  eyeGap: 1,
  mouthScale: 1,
  emblem: 'heart',
}

const FACING_VEC: Record<Direction, [number, number]> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
}

export function yawForFacing(facing: Direction): number {
  const [x, z] = FACING_VEC[facing]
  return Math.atan2(x, z)
}

function angleDelta(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from))
}

export function visualScaleForShape(bodyScale: number): number {
  return 0.8 * (0.88 + bodyScale * 0.24)
}

function shiftedColor(color: string, lightness: number): string {
  return new THREE.Color(color).offsetHSL(0, 0, lightness).getStyle()
}

function setGroupOpacity(group: THREE.Group, opacity: number) {
  group.traverse((obj) => {
    const mesh = obj as THREE.Mesh
    const material = mesh.material as THREE.Material | THREE.Material[] | undefined
    if (!material) return
    const materials = Array.isArray(material) ? material : [material]
    for (const mat of materials) {
      if ('opacity' in mat) {
        mat.transparent = opacity < 0.995
        mat.opacity = opacity
      }
    }
  })
}

function Emblem({ emblem, color }: { emblem: CharacterShape['emblem']; color: string }) {
  if (emblem === 'star') {
    return (
      <mesh position={[0, -0.18, 0.335]} rotation={[0, 0, Math.PI / 4]}>
        <octahedronGeometry args={[0.105, 0]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.55} roughness={0.25} transparent />
      </mesh>
    )
  }
  if (emblem === 'spark') {
    return (
      <mesh position={[0, -0.18, 0.335]}>
        <icosahedronGeometry args={[0.095, 1]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.55} roughness={0.25} transparent />
      </mesh>
    )
  }
  return (
    <group position={[0, -0.2, 0.34]} scale={0.62}>
      <mesh position={[-0.055, 0.045, 0]}>
        <sphereGeometry args={[0.07, 12, 8]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.4} roughness={0.35} transparent />
      </mesh>
      <mesh position={[0.055, 0.045, 0]}>
        <sphereGeometry args={[0.07, 12, 8]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.4} roughness={0.35} transparent />
      </mesh>
      <mesh position={[0, -0.03, 0]} rotation={[0, 0, Math.PI]}>
        <coneGeometry args={[0.105, 0.18, 20]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.4} roughness={0.35} transparent />
      </mesh>
    </group>
  )
}

function CharacterFace({ shape, phase, color, reducedMotion }: { shape: CharacterShape; phase: GamePhase; color: string; reducedMotion: boolean }) {
  const leftEye = useRef<THREE.Group>(null)
  const rightEye = useRef<THREE.Group>(null)
  const mouth = useRef<THREE.Mesh>(null)
  const eyeScale = 0.085 * shape.eyeScale
  const eyeGap = 0.18 * shape.eyeGap
  const smile = phase === 'dead' ? 0 : Math.PI
  useFrame((state) => {
    if (reducedMotion) return
    const t = state.clock.elapsedTime
    const blink = Math.sin(t * 1.75) > 0.965 ? 0.12 : 1
    if (leftEye.current) leftEye.current.scale.y += (blink - leftEye.current.scale.y) * 0.35
    if (rightEye.current) rightEye.current.scale.y += (blink - rightEye.current.scale.y) * 0.35
    if (mouth.current) mouth.current.rotation.z += (smile - mouth.current.rotation.z) * 0.16
  })
  return (
    <group position={[0, 0.02, 0.333]}>
      <group ref={leftEye} position={[-eyeGap, 0.095, 0]}>
        <mesh>
          <sphereGeometry args={[eyeScale, 18, 12]} />
          <meshStandardMaterial color="#1b1020" roughness={0.22} metalness={0.04} transparent />
        </mesh>
        <mesh position={[eyeScale * 0.25, eyeScale * 0.28, eyeScale * 0.72]}>
          <sphereGeometry args={[eyeScale * 0.26, 8, 6]} />
          <meshBasicMaterial color="#fff7cc" toneMapped={false} transparent />
        </mesh>
      </group>
      <group ref={rightEye} position={[eyeGap, 0.095, 0]}>
        <mesh>
          <sphereGeometry args={[eyeScale, 18, 12]} />
          <meshStandardMaterial color="#1b1020" roughness={0.22} metalness={0.04} transparent />
        </mesh>
        <mesh position={[eyeScale * 0.25, eyeScale * 0.28, eyeScale * 0.72]}>
          <sphereGeometry args={[eyeScale * 0.26, 8, 6]} />
          <meshBasicMaterial color="#fff7cc" toneMapped={false} transparent />
        </mesh>
      </group>
      <mesh position={[-eyeGap, 0.25, -0.006]} rotation={[0, 0, 0.18]}>
        <capsuleGeometry args={[0.018, 0.095, 4, 10]} />
        <meshStandardMaterial color={shiftedColor(color, -0.24)} roughness={0.45} transparent />
      </mesh>
      <mesh position={[eyeGap, 0.25, -0.006]} rotation={[0, 0, -0.18]}>
        <capsuleGeometry args={[0.018, 0.095, 4, 10]} />
        <meshStandardMaterial color={shiftedColor(color, -0.24)} roughness={0.45} transparent />
      </mesh>
      <mesh ref={mouth} position={[0, -0.09, 0.018]} rotation={[0, 0, smile]} scale={[shape.mouthScale, shape.mouthScale, 1]}>
        <torusGeometry args={[0.1, 0.014, 8, 24, Math.PI]} />
        <meshStandardMaterial color={shiftedColor(color, -0.36)} roughness={0.4} transparent />
      </mesh>
      <mesh position={[-0.27, -0.06, -0.02]}>
        <sphereGeometry args={[0.055, 14, 8]} />
        <meshStandardMaterial color="#ff9c85" emissive="#ff7f66" emissiveIntensity={0.12} roughness={0.5} transparent opacity={0.72} />
      </mesh>
      <mesh position={[0.27, -0.06, -0.02]}>
        <sphereGeometry args={[0.055, 14, 8]} />
        <meshStandardMaterial color="#ff9c85" emissive="#ff7f66" emissiveIntensity={0.12} roughness={0.5} transparent opacity={0.72} />
      </mesh>
    </group>
  )
}

function IdentityOrnaments({ characterId, color, shape, reducedMotion }: { characterId: string; color: string; shape: CharacterShape; reducedMotion: boolean }) {
  const rig = useRef<THREE.Group>(null)
  const flame = useRef<THREE.Group>(null)
  const accent = shiftedColor(color, 0.18)
  const deep = shiftedColor(color, -0.2)
  useFrame((state) => {
    const t = state.clock.elapsedTime
    if (rig.current && !reducedMotion) rig.current.rotation.y = Math.sin(t * 0.7) * 0.08
    if (flame.current && !reducedMotion) {
      const s = 1 + Math.sin(t * 8) * 0.08
      flame.current.scale.set(1 / s, s, 1 / s)
      flame.current.rotation.z = Math.sin(t * 5) * 0.08
    }
  })

  if (characterId === 'tiny_blue') {
    return (
      <group ref={rig}>
        <mesh position={[0, 0.48, -0.02]} rotation={[0.2, 0, -0.25]}>
          <capsuleGeometry args={[0.06, 0.25, 5, 12]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.42} roughness={0.2} transparent />
        </mesh>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[0.34 + i * 0.08, 0.2 + i * 0.05, -0.18 - i * 0.05]}>
            <sphereGeometry args={[0.045 - i * 0.006, 10, 8]} />
            <meshBasicMaterial color={accent} transparent opacity={0.72 - i * 0.12} toneMapped={false} />
          </mesh>
        ))}
      </group>
    )
  }

  if (characterId === 'chonky_green') {
    return (
      <group ref={rig}>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * 0.43, 0.12, 0.02]} rotation={[0, 0, s * 0.18]}>
            <sphereGeometry args={[0.15, 16, 10]} />
            <meshStandardMaterial color={deep} emissive={accent} emissiveIntensity={0.16} roughness={0.48} transparent />
          </mesh>
        ))}
        <mesh position={[0, -0.1, 0.37]} rotation={[0, 0, Math.PI / 6]}>
          <cylinderGeometry args={[0.14, 0.14, 0.035, 6]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.55} roughness={0.18} transparent />
        </mesh>
      </group>
    )
  }

  if (characterId === 'sleek_purple') {
    return (
      <group ref={rig}>
        <mesh position={[0, 0.48, 0]} rotation={[Math.PI / 2, 0, 0.2]}>
          <torusGeometry args={[0.34, 0.016, 8, 40, Math.PI * 1.45]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.75} roughness={0.25} transparent />
        </mesh>
        <mesh position={[0, -0.18, 0.37]} rotation={[0, 0, Math.PI / 4]}>
          <octahedronGeometry args={[0.1, 0]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.75} roughness={0.22} transparent />
        </mesh>
      </group>
    )
  }

  if (characterId === 'golden_glow') {
    return (
      <group ref={rig}>
        {[0, 1, 2, 3, 4].map((i) => {
          const a = (i / 5) * Math.PI * 2
          return (
            <mesh key={i} position={[Math.cos(a) * 0.19, 0.53, Math.sin(a) * 0.08]} rotation={[0, 0, a]}>
              <coneGeometry args={[0.045, 0.18, 5]} />
              <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.6} roughness={0.3} transparent />
            </mesh>
          )
        })}
      </group>
    )
  }

  if (characterId === 'aurora_luminary') {
    return (
      <group ref={rig}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[0, 0.02 + i * 0.06, 0]} rotation={[Math.PI / 2 + i * 0.34, 0.42 + i * 0.45, i * 0.85]}>
            <torusGeometry args={[0.5 + i * 0.045, 0.014, 8, 54, Math.PI * 1.55]} />
            <meshBasicMaterial color={i === 0 ? '#ff7ee8' : i === 1 ? '#7de3ff' : '#ffe68a'} transparent opacity={0.62} toneMapped={false} />
          </mesh>
        ))}
      </group>
    )
  }

  return (
    <group ref={rig}>
      <group ref={flame} position={[0, 0.56, 0.02]}>
        <mesh position={[0, 0.02, 0]} scale={[0.78, 1.18, 0.78]} rotation={[0, 0, 0.12]}>
          <sphereGeometry args={[0.12, 18, 12]} />
          <meshStandardMaterial color="#ffb454" emissive="#ff7a2f" emissiveIntensity={0.95} roughness={0.18} transparent toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.17, 0]} rotation={[0, 0, 0.12]}>
          <coneGeometry args={[0.075, 0.21, 20]} />
          <meshStandardMaterial color="#ffe08a" emissive="#ff8f3f" emissiveIntensity={0.95} roughness={0.18} transparent toneMapped={false} />
        </mesh>
        <mesh position={[0, -0.12, 0]}>
          <sphereGeometry args={[0.075, 14, 8]} />
          <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.6} roughness={0.24} transparent toneMapped={false} />
        </mesh>
        <pointLight color="#ffb454" distance={1.7} intensity={shape.bodyScale > 1 ? 0.45 : 0.7} />
      </group>
    </group>
  )
}

function CharacterBody({
  color,
  shape,
  phase,
  energy,
  reducedMotion,
  characterId,
  graphics,
}: {
  color: string
  shape: CharacterShape
  phase: GamePhase
  energy: number
  reducedMotion: boolean
  characterId: string
  graphics: 'high' | 'medium' | 'low'
}) {
  const bodyGeom = useMemo(() => makeRoundedBox(0.74, 0.78, 0.58, 0.18, 5), [])
  const displayColor = characterId === 'default_hero' && color.toLowerCase() === '#ffb454' ? '#ff982f' : color
  const bodyColor = useMemo(() => new THREE.Color(displayColor), [displayColor])
  const emissive = shiftedColor(displayColor, 0.16)
  const deep = shiftedColor(displayColor, -0.22)
  const vitality = Math.max(0.5, Math.min(1, energy))

  if (graphics === 'low') {
    return (
      <group>
        <mesh geometry={bodyGeom}>
          <meshBasicMaterial color={bodyColor} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.08, 0.318]} scale={[0.52, 0.46, 0.04]}>
          <sphereGeometry args={[0.46, 14, 8]} />
          <meshBasicMaterial color={shiftedColor(displayColor, 0.18)} transparent opacity={0.3} toneMapped={false} />
        </mesh>
        {[-1, 1].map((s) => (
          <mesh key={`eye-${s}`} position={[s * 0.18 * shape.eyeGap, 0.1, 0.34]}>
            <sphereGeometry args={[0.075 * shape.eyeScale, 10, 6]} />
            <meshBasicMaterial color="#1b1020" />
          </mesh>
        ))}
        <mesh position={[0, -0.08, 0.35]} rotation={[0, 0, Math.PI]} scale={[shape.mouthScale, shape.mouthScale, 1]}>
          <torusGeometry args={[0.09, 0.012, 6, 18, Math.PI]} />
          <meshBasicMaterial color={deep} />
        </mesh>
        <mesh position={[0, 0.58, 0.02]} scale={[0.8, 1.05, 0.8]}>
          <coneGeometry args={[0.11, 0.26, 12]} />
          <meshBasicMaterial color={emissive} toneMapped={false} />
        </mesh>
      </group>
    )
  }

  return (
    <group>
      <mesh geometry={bodyGeom} castShadow receiveShadow>
        <meshStandardMaterial
          color={bodyColor}
          emissive={emissive}
          emissiveIntensity={(characterId === 'default_hero' ? 0.58 : 0.34) * vitality}
          roughness={0.26}
          metalness={0.02}
          transparent
          opacity={0.97}
          toneMapped={false}
        />
      </mesh>
      <mesh position={[0, 0.08, 0.318]} scale={[0.52, 0.46, 0.04]}>
        <sphereGeometry args={[0.46, 24, 12]} />
        <meshBasicMaterial color={shiftedColor(displayColor, 0.24)} transparent opacity={0.24} toneMapped={false} />
      </mesh>
      {[-1, 1].map((s) => (
        <mesh key={`side-${s}`} position={[s * 0.42, -0.02, 0.02]} scale={[0.58, 0.9, 0.7]} castShadow>
          <sphereGeometry args={[0.11, 14, 10]} />
          <meshStandardMaterial color={displayColor} emissive={emissive} emissiveIntensity={0.2} roughness={0.32} transparent opacity={0.92} toneMapped={false} />
        </mesh>
      ))}
      {[-1, 1].map((s) => (
        <mesh key={`foot-${s}`} position={[s * 0.22, -0.46, 0.08]} scale={[1.35, 0.6, 0.9]} castShadow receiveShadow>
          <sphereGeometry args={[0.11, 14, 8]} />
          <meshStandardMaterial color={deep} emissive={emissive} emissiveIntensity={0.14} roughness={0.38} transparent opacity={0.94} toneMapped={false} />
        </mesh>
      ))}
      <CharacterFace shape={shape} phase={phase} color={displayColor} reducedMotion={reducedMotion} />
      <Emblem emblem={shape.emblem} color={shiftedColor(displayColor, 0.2)} />
      <IdentityOrnaments characterId={characterId} color={displayColor} shape={shape} reducedMotion={reducedMotion} />
    </group>
  )
}

export function Ember({
  color,
  phase,
  slideDir,
  facing,
  reducedMotion,
  shape,
  bonkKey = 0,
  energy = 1,
  characterId = 'default_hero',
  billboard = true,
  equippedProps = [],
  graphics = 'high',
}: EmberProps) {
  const sh = shape ?? DEFAULT_SHAPE
  const group = useRef<THREE.Group>(null)
  const billboardRef = useRef<THREE.Group>(null)
  const animatedRef = useRef<THREE.Group>(null)
  const glowMat = useRef<THREE.MeshBasicMaterial>(null)
  const light = useRef<THREE.PointLight>(null)

  const exitT = useRef(0)
  const lastPhase = useRef<GamePhase>(phase)
  const bonkT = useRef(0)
  const lastBonk = useRef(bonkKey)
  const entryT = useRef(0)
  const hopT = useRef(0)
  const lastPos = useRef(0)
  const faceMoveT = useRef(0)
  const bodyOpacity = useRef(-1)

  const glowColor = useMemo(() => new THREE.Color(color), [color])

  const size = visualScaleForShape(sh.bodyScale)
  const baseY = 0.34
  const anchor = size * 0.44

  useFrame((state, delta) => {
    const g = group.current
    const bb = billboardRef.current
    const a = animatedRef.current
    if (!g || !bb || !a) return
    const t = state.clock.elapsedTime
    glowColor.set(color)
    if (glowMat.current) glowMat.current.color.copy(glowColor)
    if (light.current) light.current.color.copy(glowColor)

    const posMarker = g.position.x + g.position.z
    const posChanged = Math.abs(posMarker - lastPos.current) > 0.02
    lastPos.current = posMarker
    if (phase === 'sliding' || posChanged) faceMoveT.current = 1.35
    else if (phase === 'idle') faceMoveT.current = Math.max(0, faceMoveT.current - delta)

    if (billboard && !(phase === 'won')) {
      const cam = state.camera
      const targetYaw = phase === 'sliding' || faceMoveT.current > 0.001
        ? yawForFacing(facing)
        : Math.atan2(cam.position.x - g.position.x, cam.position.z - g.position.z)
      bb.rotation.y += angleDelta(bb.rotation.y, targetYaw) * Math.min(1, delta * (phase === 'sliding' ? 12 : 4))
    }

    if (phase !== lastPhase.current) {
      if (phase === 'dead' || phase === 'won') exitT.current = 0
      if (phase === 'intro') entryT.current = 0
      lastPhase.current = phase
    }
    if (bonkKey !== lastBonk.current) {
      lastBonk.current = bonkKey
      bonkT.current = 1
    }
    if (bonkT.current > 0.001) bonkT.current = Math.max(0, bonkT.current - delta * 3)

    const vitality = Math.max(0.5, Math.min(1, energy))

    if (phase === 'dead') {
      const speed = reducedMotion ? 4 : 2.4
      exitT.current = Math.min(1, exitT.current + delta * speed)
      const e = exitT.current
      const squash = e < 0.4 ? 1 - (e / 0.4) * 0.7 : 0.3
      const spread = e < 0.4 ? 1 + (e / 0.4) * 0.4 : 1.4
      const fade = Math.max(0, 1 - Math.max(0, e - 0.35) / 0.55)
      a.scale.set(size * spread, size * Math.max(0.05, squash), size)
      g.position.y = baseY - e * 0.32
      g.rotation.z = e * 0.5
      if (Math.abs(bodyOpacity.current - fade) > 0.01) {
        setGroupOpacity(a, fade)
        bodyOpacity.current = fade
      }
      if (glowMat.current) glowMat.current.opacity = 0.5 * fade
      if (light.current) light.current.intensity = Math.max(0, (1 - e) * 5)
      return
    }

    if (phase === 'won') {
      const speed = reducedMotion ? 4 : 1.8
      exitT.current = Math.min(1, exitT.current + delta * speed)
      const e = exitT.current
      const pop = 1 + Math.sin(Math.min(Math.PI, e * Math.PI)) * 0.25
      a.scale.set(size * pop, size * pop, size * pop)
      g.position.y = baseY + e * 0.85
      bb.rotation.y += delta * (3 + e * 8)
      const fade = Math.max(0, 1 - Math.max(0, e - 0.55) / 0.45)
      if (Math.abs(bodyOpacity.current - fade) > 0.01) {
        setGroupOpacity(a, fade)
        bodyOpacity.current = fade
      }
      if (glowMat.current) glowMat.current.opacity = 0.65 * fade
      if (light.current) light.current.intensity = 2.6 + e * 5
      return
    }

    if (bodyOpacity.current !== 1) {
      setGroupOpacity(a, 1)
      bodyOpacity.current = 1
    }
    if (glowMat.current) glowMat.current.opacity = 0.5 + Math.sin(t * 2.5) * 0.08

    entryT.current = Math.min(1, entryT.current + delta * (reducedMotion ? 6 : 3.6))
    const pop = phase === 'intro' ? entryT.current : 1
    g.rotation.z = 0

    const sliding = phase === 'sliding'
    if (reducedMotion) {
      a.scale.set(size * pop, size * pop, size * pop)
      g.position.y = baseY
    } else if (sliding && slideDir) {
      const [sdx, sdy] = slideDir
      const jelly = 1 + bonkT.current * 0.16
      const stretch = 1.24
      const squash = 0.84
      a.scale.set(size * (sdx !== 0 ? stretch : squash) * pop, size * squash * jelly * pop, size * (sdy !== 0 ? stretch : 1) * pop)
      g.position.y = baseY
      g.rotation.z += (-sdx * 0.16 - g.rotation.z) * Math.min(1, delta * 10)
    } else {
      const jelly = 1 + bonkT.current * 0.2
      const breathe = 1 + Math.sin(t * 2.4) * 0.05 * vitality
      if (posChanged && hopT.current <= 0) hopT.current = 1
      if (hopT.current > 0) hopT.current = Math.max(0, hopT.current - delta * 4)
      const hop = Math.sin(hopT.current * Math.PI) * 0.14
      a.scale.set(size * (2 - breathe) * jelly * pop, size * breathe * jelly * pop, size * (1 + (breathe - 1) * 0.45) * pop)
      g.position.y = baseY + Math.sin(t * 2.2) * 0.035 + hop
      const [fx] = FACING_VEC[facing]
      g.rotation.z += (-fx * 0.05 - g.rotation.z) * Math.min(1, delta * 5)
    }

    if (light.current) light.current.intensity = (2.4 + Math.sin(t * 3) * 0.4) * vitality
  })

  return (
    <group ref={group} position={[0, baseY, 0]}>
      <group ref={billboardRef} position={[0, anchor, 0]}>
        <mesh position={[0, 0, -0.42]}>
          <planeGeometry args={[size * 1.65, size * 1.65]} />
          <meshBasicMaterial
            ref={glowMat}
            color={color}
            map={getRadialGlow()}
            transparent
            opacity={0.5}
            depthWrite={false}
            blending={THREE.AdditiveBlending}
            toneMapped={false}
          />
        </mesh>
        <group ref={animatedRef} scale={[size, size, size]}>
          <EmberProps3D equippedProps={equippedProps} bodyScale={sh.bodyScale} slots={['back']} depth={-0.36} />
          <CharacterBody color={color} shape={sh} phase={phase} energy={energy} reducedMotion={reducedMotion} characterId={characterId} graphics={graphics} />
          <EmberProps3D equippedProps={equippedProps} bodyScale={sh.bodyScale} slots={['head', 'hair', 'face', 'hand', 'accessory']} depth={0.04} />
        </group>
      </group>
      {graphics !== 'low' && <pointLight ref={light} color={color} distance={4.5} intensity={2.4} />}
    </group>
  )
}

let radialGlow: THREE.Texture | null = null

function getRadialGlow(): THREE.Texture | null {
  if (radialGlow) return radialGlow
  if (typeof document === 'undefined') return null
  const s = 128
  const c = document.createElement('canvas')
  c.width = c.height = s
  const ctx = c.getContext('2d')
  if (!ctx) return null
  const grad = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2)
  grad.addColorStop(0, 'rgba(255,255,255,0.9)')
  grad.addColorStop(0.4, 'rgba(255,255,255,0.35)')
  grad.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, s, s)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  radialGlow = tex
  return tex
}
